/**
 * Types e interfaces para o sistema de Pipelines Segmentadas
 */

export type PipelineType = 'qualificacao' | 'venda' | 'pos_venda';

export const PIPELINE_TYPE_LABELS: Record<PipelineType, string> = {
  qualificacao: 'Qualificação',
  venda: 'Comercial',
  pos_venda: 'Pós-venda',
};

export const PIPELINE_TYPE_OPTIONS: { value: PipelineType; label: string }[] = [
  { value: 'qualificacao', label: PIPELINE_TYPE_LABELS.qualificacao },
  { value: 'venda', label: PIPELINE_TYPE_LABELS.venda },
  { value: 'pos_venda', label: PIPELINE_TYPE_LABELS.pos_venda },
];

/** Ordem do processo padrão: Qualificação → Comercial → Pós-venda */
export const PIPELINE_TYPE_ORDER: Record<PipelineType, number> = {
  qualificacao: 0,
  venda: 1,
  pos_venda: 2,
};
export type PipelinePerfilUsuario =
  | 'relacionador'
  | 'lider_vendas'
  | 'supervisor'
  | 'especialista_pos_venda'
  | 'admin';
export type CardAction = 'criado' | 'movido_para_stage' | 'editado' | 'deletado' | 'transferido';
export type StageVisibility = 'publico' | 'relacionador' | 'lider' | 'supervisor' | 'admin';

/**
 * Representa uma coluna (etapa) dentro de uma pipeline
 */
export interface PipelineStage {
  id: string;
  pipeline_id: string;
  nome: string;
  ordem: number;
  cor?: string;
  campos_obrigatorios?: string[];
  visibilidade: StageVisibility;
  /** Etapa fora da linha do tempo (ex.: Fixado/Perdido) — rodapé no drag; coluna só com toggle */
  oculta?: boolean;
  comissao_valor?: number;
  comissao_percentual?: number;
  created_at?: string;
  updated_at?: string;
}

/**
 * Representa uma tag reutilizável, com escopo de tenant (não por pipeline).
 * Referenciada por id em PipelineCard.dados_card.tags.
 */
export interface PipelineTag {
  id: string;
  tenant_id: number;
  nome: string;
  cor: string;
  criado_por: string | null;
  created_at?: string;
  updated_at?: string;
}

/**
 * Representa um card (cliente/lead) dentro de uma etapa
 */
export interface PipelineCard {
  id: string;
  pipeline_id: string;
  stage_id: string;
  tenant_id: number;
  cliente_id?: number;
  criado_por: string;
  responsavel_id: string;
  dados_card: {
    nome: string;
    telefone?: string;
    interesse?: string;
    valor?: number;
    hora_agendamento?: string;
    agendamento_id?: number;
    /** Array de ids (uuid) referenciando PipelineTag.id — não texto livre */
    tags?: string[];
    [key: string]: unknown;
  };
  contrato_id?: number;
  contratos?: {
    id: number;
    valor_bem: number;
    status: string;
    grupo_id?: number;
    bem_id?: number;
    cliente_id?: number;
    cota?: string | null;
    grupos?: {
      numero_grupo: number;
      especie: string;
      titulo: string;
      data_proxima_assembleia?: string | null;
      data_vencimento_grupo?: string | null;
    };
    bens?: {
      codigo_bem: number;
      tipo_bem: string;
    };
    clientes?: {
      nome: string;
    };
  };
  data_criacao: string;
  data_ultima_atualizacao: string;
  data_para_agendamento?: string;
  cancelado: boolean;
  motivo_cancelamento?: string;
  comissao_acumulada?: number;
  created_at?: string;
  updated_at?: string;
}

/**
 * Representa o histórico de mudanças em um card
 */
export interface CardHistory {
  id: string;
  card_id: string;
  usuario_id: string;
  acao: CardAction;
  stage_anterior_id?: string;
  stage_novo_id?: string;
  dados_alterados?: Record<string, unknown>;
  motivo?: string;
  created_at: string;
}

/**
 * Representa uma pipeline (kanban board)
 */
export interface Pipeline {
  id: string;
  tenant_id: number;
  nome: string;
  tipo: PipelineType;
  representacao_id?: number;
  perfil_usuario: PipelinePerfilUsuario | PipelinePerfilUsuario[];
  tenant_ids?: number[];
  permitir_associados?: boolean;
  ativa: boolean;
  /** Pipeline fixa do sistema (Comercial/Qualificação/Pós-Venda): não pode ser excluída, desativada, renomeada, nem ter o tipo alterado. */
  fixa: boolean;
  criada_por?: string;
  created_at?: string;
  updated_at?: string;
  stages?: PipelineStage[];
  cards?: PipelineCard[];
}

/**
 * Representa uma stage com seus cards (para view em Kanban)
 */
export interface StageWithCards {
  stage: PipelineStage;
  cards: PipelineCard[];
  count: number;
}

/**
 * Representa o estado completo de uma pipeline (para view em Kanban)
 */
export interface KanbanBoard {
  pipeline: Pipeline;
  stages: StageWithCards[];
  totalCards: number;
  lastUpdated: Date;
}

/**
 * Props para movimentação de card entre stages
 */
export interface MoveCardRequest {
  cardId: string;
  pipelineId: string;
  stageId: string;
  stagAnteriorId?: string;
  motivo?: string;
  usuarioId: string;
}

/**
 * Props para criação de novo card
 */
export interface CreateCardRequest {
  pipelineId: string;
  stageId: string;
  tenantId: number;
  responsavelId: string;
  dadosCard: {
    nome: string;
    telefone?: string;
    interesse?: string;
    valor?: number;
    [key: string]: unknown;
  };
  clienteId?: number;
  dataParaAgendamento?: string;
  /** Hora no formato HH:mm — espelha agendamentos.hora_agendamento */
  horaAgendamento?: string;
  /** ISO datetime editável pelo usuário — vai para data_criacao (created_at permanece automático, só auditoria) */
  dataCriacao?: string;
}

/**
 * Props para atualização de card
 */
export interface UpdateCardRequest {
  cardId: string;
  dadosCard?: Partial<PipelineCard['dados_card']>;
  stageId?: string;
  responsavelId?: string;
  /** ISO datetime editável pelo usuário — atualiza data_criacao (created_at permanece automático, só auditoria) */
  dataCriacao?: string;
  /** Data (YYYY-MM-DD) do agendamento vinculado — atualiza data_para_agendamento */
  dataParaAgendamento?: string;
  /** Merge sobre o dados_card atual do banco (em vez de substituir o objeto inteiro) */
  dadosCardPatch?: Partial<PipelineCard['dados_card']>;
}
